import { useTranslation } from 'react-i18next';
import { getObjectColors } from '@/utils/objectColors';
import { fmtHours, fmtRange, fmtTime, isAvailable, objectColorKey, unavailableText } from '../assistantFormat';
import type { AssistantEmployee } from '../assistantTypes';

export interface DayLineGhost {
  start: number;
  end: number;
  travelIn: number;
  objectId?: string | null;
  objectName?: string | null;
  objectShortName?: string | null;
}

interface Props {
  employee: AssistantEmployee;
  ghost?: DayLineGhost | null;
  /** Tall (52px, with time + object labels) or compact (30px, no labels). */
  size?: 'tall' | 'compact';
  ticks?: boolean;
  newlyAssignedTaskIds?: string[];
  /** Hover on a planned block; `null` when the pointer leaves it. */
  onBlockHover?: (planningEntryTaskId: string | null) => void;
  /** Block drawn with a ring (e.g. its stop is hovered on the map). */
  highlightedBlockId?: string | null;
}

const DEFAULT_FROM = 6 * 60;
const DEFAULT_TO = 20 * 60;

function range(e: AssistantEmployee): [number, number] {
  const starts = [e.workStart ?? DEFAULT_FROM, ...(e.blocks ?? []).map((b) => b.start ?? DEFAULT_FROM)];
  const ends = [e.workEnd ?? DEFAULT_TO, ...(e.blocks ?? []).map((b) => b.end ?? DEFAULT_TO)];
  const from = Math.min(DEFAULT_FROM, Math.floor(Math.min(...starts) / 120) * 120);
  const to = Math.max(DEFAULT_TO, Math.ceil(Math.max(...ends) / 120) * 120);
  return [Math.max(0, from), Math.min(24 * 60, to)];
}

/** Day timeline 06–20 h: working window, planned blocks, free gaps and an optional preview. */
export function DayLine({
  employee,
  ghost,
  size = 'tall',
  ticks = false,
  newlyAssignedTaskIds = [],
  onBlockHover,
  highlightedBlockId,
}: Props) {
  const { t, i18n } = useTranslation('planning');
  const tall = size === 'tall';
  const heightClass = tall ? 'h-[52px]' : 'h-[30px]';

  if (!isAvailable(employee)) {
    const label = employee.absenceTypeName
      ? t('assistant.dayLine.allDay', { reason: employee.absenceTypeName })
      : unavailableText(t, employee);
    return (
      <div
        className={`${heightClass} rounded-md grid place-items-center text-[10px] uppercase tracking-wider text-slate-500 bg-[repeating-linear-gradient(135deg,theme(colors.slate.50)_0_6px,theme(colors.slate.200)_6px_12px)]`}
      >
        {label}
      </div>
    );
  }

  const [from, to] = range(employee);
  const total = to - from;
  const pct = (m: number) => ((Math.min(to, Math.max(from, m)) - from) / total) * 100;
  const pos = (a: number, b: number) => ({ left: `${pct(a)}%`, width: `${Math.max(0, pct(b) - pct(a))}%` });
  const tickHours: number[] = [];
  for (let h = from / 60; h <= to / 60; h += 2) tickHours.push(h);

  const ghostColors = ghost ? getObjectColors(objectColorKey(ghost.objectId, ghost.objectName)) : null;

  return (
    <div>
      {ticks && (
        <div className="relative h-4 mb-1">
          {tickHours.map((h) => (
            <span
              key={h}
              className="absolute text-[10px] text-slate-400 tabular-nums -translate-x-1/2"
              style={{ left: `${pct(h * 60)}%` }}
            >
              {String(h).padStart(2, '0')}
            </span>
          ))}
        </div>
      )}
      <div className={`relative ${heightClass} rounded-md bg-slate-50 border border-slate-100`}>
        <div
          className="absolute inset-y-0 bg-white border-x border-slate-200"
          style={pos(employee.workStart ?? from, employee.workEnd ?? to)}
        />
        {(employee.gaps ?? []).map((g, i) => (
          <div
            key={`g${i}`}
            className="absolute inset-y-1 rounded border border-dashed border-slate-300 grid place-items-center overflow-hidden"
            style={pos(g.from ?? 0, g.to ?? 0)}
          >
            {tall && !ghost && (g.to ?? 0) - (g.from ?? 0) >= 60 && (
              <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                {t('assistant.dayLine.free', { hours: fmtHours((g.to ?? 0) - (g.from ?? 0), i18n.language) })}
              </span>
            )}
          </div>
        ))}
        {(employee.blocks ?? []).map((b, i) => {
          const { color, soft, ink } = getObjectColors(objectColorKey(b.objectId, b.objectName));
          const isNew = !!b.taskId && newlyAssignedTaskIds.includes(b.taskId);
          const id = b.planningEntryTaskId ?? '';
          const highlighted = !!id && id === highlightedBlockId;
          const hover = id ? onBlockHover : undefined;
          return (
            <div
              key={id || `b${i}`}
              className="absolute inset-y-1 rounded px-1.5 flex flex-col justify-center overflow-hidden"
              title={`${fmtRange(b.start, b.end)} ${b.objectShortName || b.objectName || ''}`}
              onMouseEnter={hover ? () => hover(id) : undefined}
              onMouseLeave={hover ? () => hover(null) : undefined}
              style={{
                ...pos(b.start ?? 0, b.end ?? 0),
                background: soft,
                color: ink,
                borderLeft: `3px solid ${color}`,
                boxShadow: highlighted
                  ? `inset 0 0 0 2px ${color}`
                  : isNew
                    ? `inset 0 0 0 1px ${color}`
                    : undefined,
              }}
            >
              {tall && <span className="text-[9.5px] tabular-nums truncate opacity-80">{fmtTime(b.start)}</span>}
              {tall && <span className="text-[10.5px] font-semibold truncate">{b.objectShortName || b.objectName}</span>}
            </div>
          );
        })}
        {ghost && ghostColors && (
          <>
            {ghost.travelIn > 0 && (
              <div
                className="absolute inset-y-[45%] rounded-full bg-slate-300"
                style={pos(ghost.start - ghost.travelIn, ghost.start)}
                title={t('assistant.dayLine.travel')}
              />
            )}
            <div
              className="absolute inset-y-0.5 rounded px-1.5 flex flex-col justify-center overflow-hidden"
              style={{
                ...pos(ghost.start, ghost.end),
                background: `repeating-linear-gradient(135deg, ${ghostColors.soft} 0 5px, white 5px 10px)`,
                color: ghostColors.ink,
                border: `1.5px dashed ${ghostColors.color}`,
              }}
            >
              {tall && <span className="text-[9.5px] tabular-nums truncate">{fmtRange(ghost.start, ghost.end)}</span>}
              {tall && (
                <span className="text-[10.5px] font-semibold truncate">{ghost.objectShortName || ghost.objectName}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
